import React, { useState, useRef, useEffect } from 'react';
import { 
  Pen, Type, Upload, Trash, Save, Edit3, Plus, Settings, 
  Check, AlertCircle, FileSignature, ChevronRight, X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

// Use the same design system colors from Dashboard.jsx
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

// Available fonts for typed signatures (complete library from settings)
const availableFonts = [
  // Professional & Business
  { name: 'Inter', css: "'Inter', sans-serif", category: 'professional' },
  { name: 'Roboto', css: "'Roboto', sans-serif", category: 'professional' },
  { name: 'Open Sans', css: "'Open Sans', sans-serif", category: 'professional' },
  { name: 'Lato', css: "'Lato', sans-serif", category: 'professional' },
  { name: 'Montserrat', css: "'Montserrat', sans-serif", category: 'professional' },
  { name: 'Poppins', css: "'Poppins', sans-serif", category: 'professional' },
  { name: 'Source Sans Pro', css: "'Source Sans Pro', sans-serif", category: 'professional' },
  
  // Elegant Scripts
  { name: 'Dancing Script', css: "'Dancing Script', cursive", category: 'elegant' },
  { name: 'Great Vibes', css: "'Great Vibes', cursive", category: 'elegant' },
  { name: 'Allura', css: "'Allura', cursive", category: 'elegant' },
  { name: 'Pinyon Script', css: "'Pinyon Script', cursive", category: 'elegant' },
  
  // Modern Scripts
  { name: 'Alex Brush', css: "'Alex Brush', cursive", category: 'modern' },
  { name: 'Kaushan Script', css: "'Kaushan Script', cursive", category: 'modern' },
  { name: 'Yellowtail', css: "'Yellowtail', cursive", category: 'modern' },
  
  // Casual Handwriting
  { name: 'Pacifico', css: "'Pacifico', cursive", category: 'casual' },
  { name: 'Satisfy', css: "'Satisfy', cursive", category: 'casual' },
  { name: 'Caveat', css: "'Caveat', cursive", category: 'casual' },
  { name: 'Indie Flower', css: "'Indie Flower', cursive", category: 'casual' },
  
  // Classic & Professional
  { name: 'Sacramento', css: "'Sacramento', cursive", category: 'classic' },
  { name: 'Norican', css: "'Norican', cursive", category: 'classic' },
  { name: 'Kalam', css: "'Kalam', sans-serif", category: 'classic' },
];

const DefaultSignatureDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // States
  const [defaultSignature, setDefaultSignature] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [signatureType, setSignatureType] = useState('drawn');
  const [typedSignature, setTypedSignature] = useState('');
  const [typedSignatureFont, setTypedSignatureFont] = useState('Inter');
  const [fontCategory, setFontCategory] = useState('professional');
  const [imageSignature, setImageSignature] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Refs
  const canvasRef = useRef(null);
  const contextRef = useRef(null);

  useEffect(() => {
    // Load Google Fonts
    if (!document.head.querySelector('link[data-google-fonts]')) {
      document.head.insertAdjacentHTML('beforeend', `
        <link data-google-fonts href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Open+Sans:wght@400;600;700&family=Lato:wght@400;700&family=Montserrat:wght@400;600;700&family=Poppins:wght@400;500;600;700&family=Source+Sans+Pro:wght@400;600;700&family=Dancing+Script&family=Great+Vibes&family=Pacifico&family=Sacramento&family=Satisfy&family=Allura&family=Alex+Brush&family=Kaushan+Script&family=Yellowtail&family=Pinyon+Script&family=Caveat&family=Indie+Flower&family=Norican&family=Kalam:wght@400;700&display=swap" rel="stylesheet">
      `);
    }
    
    fetchDefaultSignature();
  }, []);

  // Initialize canvas when switching to drawing mode
  useEffect(() => {
    if (signatureType === 'drawn' && showEditModal) {
      setTimeout(() => {
        initializeCanvas();
      }, 100);
    }
  }, [signatureType, showEditModal]);

  // Effect to automatically select first font when category changes
  useEffect(() => {
    const filteredFonts = availableFonts.filter(font => font.category === fontCategory);
    if (filteredFonts.length > 0 && !filteredFonts.find(f => f.name === typedSignatureFont)) {
      setTypedSignatureFont(filteredFonts[0].name);
    }
  }, [fontCategory, typedSignatureFont]);

  const fetchDefaultSignature = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/users/default-signature`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch default signature');
      }
      
      const data = await response.json();
      setDefaultSignature(data.defaultSignature);
    } catch (error) {
      console.error('Error fetching default signature:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const initializeCanvas = () => {
    if (canvasRef.current && !contextRef.current) {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      
      const context = canvas.getContext('2d');
      context.scale(2, 2);
      context.lineCap = 'round';
      context.strokeStyle = '#0F172A';
      context.lineWidth = 2;
      contextRef.current = context;
    }
  };

  const startDrawing = ({ nativeEvent }) => {
    if (!contextRef.current) {
      initializeCanvas();
    }
    
    if (!contextRef.current) return;
    
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing || !contextRef.current) return;
    
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.lineTo(offsetX, offsetY);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    if (!contextRef.current) return;
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (contextRef.current && canvasRef.current) {
      contextRef.current.clearRect(0, 0, canvasRef.current.width / 2, canvasRef.current.height / 2);
      setHasDrawn(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target.result;
      setImageSignature(imageData);
      setImagePreview(imageData);
    };
    reader.readAsDataURL(file);
  };

  const createTypedSignatureImage = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    
    // Set background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set text style
    const selectedFont = availableFonts.find(f => f.name === typedSignatureFont);
    ctx.fillStyle = '#0F172A';
    ctx.font = `36px ${selectedFont?.css || "'Inter', sans-serif"}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw text
    ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
    
    return canvas.toDataURL('image/png');
  };

  const saveSignature = async () => {
    setIsSaving(true);
    try {
      let signatureData;
      let signatureFont = null;
      
      if (signatureType === 'drawn') {
        if (!hasDrawn) {
          toast.error('Please draw your signature first');
          return;
        }
        signatureData = canvasRef.current.toDataURL('image/png');
      } else if (signatureType === 'typed') {
        if (!typedSignature.trim()) {
          toast.error('Please enter your signature text');
          return;
        }
        signatureData = createTypedSignatureImage();
        signatureFont = typedSignatureFont;
      } else if (signatureType === 'image') {
        if (!imageSignature) {
          toast.error('Please upload a signature image');
          return;
        }
        signatureData = imageSignature;
      }
      
      const payload = {
        signatureType,
        signatureData,
        signatureFont,
        originalText: signatureType === 'typed' ? typedSignature : null
      };
      
      const response = await fetch(`${API_URL}/users/default-signature`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save signature');
      }
      
      const data = await response.json();
      setDefaultSignature(data.defaultSignature);
      setShowEditModal(false);
      toast.success('Default signature saved successfully!');
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Failed to save signature. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSignature = async () => {
    if (!window.confirm('Are you sure you want to delete your default signature?')) {
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/users/default-signature`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete signature');
      }
      
      setDefaultSignature(null);
      setShowEditModal(false);
      toast.success('Default signature deleted successfully!');
    } catch (error) {
      console.error('Error deleting signature:', error);
      toast.error('Failed to delete signature. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getFilteredFonts = () => {
    return availableFonts.filter(font => font.category === fontCategory);
  };

  const handleEdit = () => {
    if (defaultSignature) {
      const sig = defaultSignature;
      setSignatureType(sig.type);
      if (sig.type === 'typed') {
        setTypedSignature(sig.originalText || '');
        setTypedSignatureFont(sig.font || 'Inter');
        // Set category based on font
        const font = availableFonts.find(f => f.name === sig.font);
        if (font) {
          setFontCategory(font.category);
        }
      } else if (sig.type === 'image' || sig.type === 'drawn') {
        setImageSignature(sig.data);
        setImagePreview(sig.data);
      }
    } else {
      setSignatureType('drawn');
      setTypedSignature('');
      setFontCategory('professional');
      setImageSignature(null);
      setImagePreview(null);
      setHasDrawn(false);
      clearCanvas();
    }
    setShowEditModal(true);
  };

  return (
    <>
      {/* Card that matches stats cards design */}
      <div 
        className="p-4 rounded-3xl border transition-all duration-200 ease-out cursor-pointer group"
        style={{
          background: 'linear-gradient(135deg, rgba(52, 199, 89, 0.05), rgba(30, 64, 175, 0.02))',
          borderColor: colors.gray200,
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)',
          borderRadius: '24px',
          fontFamily: fontStyles.fontFamily
        }}
        onClick={handleEdit}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.1), 0 6px 15px rgba(0, 0, 0, 0.07)';
          e.currentTarget.style.borderColor = colors.success;
          e.currentTarget.style.filter = 'brightness(1.01)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)';
          e.currentTarget.style.borderColor = colors.gray200;
          e.currentTarget.style.filter = 'brightness(1)';
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <p 
              className="text-sm font-medium mb-2"
              style={{
                color: colors.gray500,
                ...fontStyles.medium,
                letterSpacing: '-0.005em'
              }}
            >
              Default Signature
            </p>
            {/* Main focal element matching other cards */}
            {defaultSignature ? (
              <div className="mb-4">
                <img 
                  src={defaultSignature.data} 
                  alt="Default signature" 
                  className="max-h-16 max-w-full"
                  style={{
                    filter: 'contrast(1.2) brightness(1.1) saturate(1.1)',
                    objectFit: 'contain',
                    background: 'transparent',
                    WebkitFilter: 'contrast(1.2) brightness(1.1) saturate(1.1)'
                  }}
                />
              </div>
            ) : (
              <div className="mb-4 flex items-center">
                <Plus size={32} className="mr-3 text-gray-400" />
                <span 
                  className="text-2xl font-medium"
                  style={{
                    color: colors.gray500,
                    ...fontStyles.medium
                  }}
                >
                  Create
                </span>
              </div>
            )}
          </div>
          
          <div className="flex flex-col items-end space-y-2">
            <div 
              className="flex items-center text-xs font-medium px-2 py-1 rounded-full"
              style={{
                backgroundColor: defaultSignature ? colors.successLight : colors.gray100,
                color: defaultSignature ? colors.success : colors.gray500
              }}
            >
              {defaultSignature ? "✓ Ready" : "+ Create"}
            </div>
            <div 
              className="p-4 rounded-2xl transition-all duration-300"
              style={{
                background: `linear-gradient(135deg, ${colors.successLight}, rgba(52, 199, 89, 0.15))`,
                boxShadow: `0 8px 20px ${colors.success}20`
              }}
            >
              <FileSignature 
                size={24} 
                className="transition-all duration-300"
                style={{color: colors.success}}
              />
            </div>
          </div>
        </div>
        
        <div 
          className="text-sm flex items-center justify-between pt-4 border-t"
          style={{
            borderColor: colors.gray100,
            color: colors.gray600,
            ...fontStyles.body
          }}
        >
          <span 
            className="font-medium"
            style={{
              color: defaultSignature ? colors.success : colors.gray500,
              ...fontStyles.medium
            }}
          >
            {defaultSignature ? (
              `${defaultSignature.type} signature`
            ) : (
              "Click to set up"
            )}
          </span>
          <div 
            className="w-2 h-2 rounded-full"
            style={{
              backgroundColor: defaultSignature ? colors.success : colors.gray300,
              boxShadow: defaultSignature 
                ? `0 0 8px ${colors.success}40` 
                : 'none'
            }}
          />
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {defaultSignature ? 'Edit Signature' : 'Create Signature'}
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Signature Type Tabs */}
            <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setSignatureType('drawn')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  signatureType === 'drawn'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Pen size={16} className="mx-auto mb-1" />
                Draw
              </button>
              <button
                onClick={() => setSignatureType('typed')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  signatureType === 'typed'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Type size={16} className="mx-auto mb-1" />
                Type
              </button>
              <button
                onClick={() => setSignatureType('image')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  signatureType === 'image'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Upload size={16} className="mx-auto mb-1" />
                Upload
              </button>
            </div>

            {/* Signature Creation Content */}
            {signatureType === 'drawn' && (
              <div className="space-y-4 mb-6">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-3">Draw your signature below:</p>
                  <canvas
                    ref={canvasRef}
                    className="border border-gray-200 rounded-md bg-white cursor-crosshair w-full"
                    style={{ width: '100%', height: '150px' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                  />
                  <div className="flex justify-between items-center mt-3">
                    <button
                      onClick={clearCanvas}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 flex items-center"
                    >
                      <Trash size={14} className="mr-1" />
                      Clear
                    </button>
                    <p className="text-xs text-gray-500">
                      {hasDrawn ? 'Signature drawn' : 'Click and drag to draw'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {signatureType === 'typed' && (
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Signature Text
                  </label>
                  <input
                    type="text"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Font Style
                  </label>
                  <div className="flex space-x-2 mb-3">
                    {['professional', 'elegant', 'modern', 'casual', 'classic'].map((category) => (
                      <button
                        key={category}
                        onClick={() => setFontCategory(category)}
                        className={`px-3 py-1 text-sm rounded-md capitalize ${
                          fontCategory === category
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                  
                  <select
                    value={typedSignatureFont}
                    onChange={(e) => setTypedSignatureFont(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getFilteredFonts().map((font) => (
                      <option key={font.name} value={font.name}>
                        {font.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {typedSignature && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-600 mb-2">Preview:</p>
                    <div 
                      className="text-3xl text-center py-4"
                      style={{ 
                        fontFamily: availableFonts.find(f => f.name === typedSignatureFont)?.css || "'Inter', sans-serif" 
                      }}
                    >
                      {typedSignature}
                    </div>
                  </div>
                )}
              </div>
            )}

            {signatureType === 'image' && (
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Signature Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                {imagePreview && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-600 mb-2">Preview:</p>
                    <div className="flex justify-center">
                      <img 
                        src={imagePreview} 
                        alt="Signature preview" 
                        className="max-h-32 max-w-full border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between">
              <div>
                {defaultSignature && (
                  <button
                    onClick={deleteSignature}
                    disabled={isSaving}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                  >
                    <Trash size={16} className="mr-2" />
                    Delete
                  </button>
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveSignature}
                  disabled={isSaving}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  <Save size={16} className="mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DefaultSignatureDashboard;